/**
 * Source de vérité des langues du site.
 *
 * Le même tableau sert au sélecteur de langue (drapeau), à la config i18next et
 * au routage : `/fr`, `/en`, `/es`, `/it`, `/de`, `/pt`. On garde le code ISO
 * 639-1 comme segment d'URL (et non un code pays type `/us`) parce que c'est ce
 * que Google attend en `hreflang` et que le contenu cible une langue, pas un pays.
 */

export interface AppLanguage {
  /** Code i18next / segment d'URL. */
  code: string;
  /** Libellé natif affiché dans le sélecteur. */
  label: string;
  /** Nom du SVG dans `src/assets` (le drapeau du pays, pas de la langue). */
  flagCode: string;
  /** Valeur `hreflang` / attribut `lang` du document. */
  hreflang: string;
}

export const APP_LANGUAGES: AppLanguage[] = [
  { code: 'fr', label: 'Français', flagCode: 'fr', hreflang: 'fr' },
  { code: 'en', label: 'English', flagCode: 'us', hreflang: 'en' },
  { code: 'es', label: 'Español', flagCode: 'es', hreflang: 'es' },
  { code: 'it', label: 'Italiano', flagCode: 'it', hreflang: 'it' },
  { code: 'de', label: 'Deutsch', flagCode: 'de', hreflang: 'de' },
  { code: 'pt', label: 'Português', flagCode: 'pt', hreflang: 'pt' },
];

export const SUPPORTED_LANGUAGES = APP_LANGUAGES.map(l => l.code);
export const DEFAULT_LANGUAGE = 'fr';
export const LANGUAGE_STORAGE_KEY = 'tradetracker_language';

/** Ramène n'importe quelle étiquette BCP 47 (`fr-FR`, `pt-BR`, `EN_us`…) sur une langue supportée. */
export function normalizeLanguage(tag: string | null | undefined): string | null {
  if (!tag) return null;
  const base = tag.toLowerCase().replace('_', '-').split('-')[0];
  return SUPPORTED_LANGUAGES.includes(base) ? base : null;
}

/** Langue lue dans le premier segment du chemin (`/es/...` → `es`), sinon `null`. */
export function languageFromPath(pathname: string = window.location.pathname): string | null {
  const segment = pathname.split('/').filter(Boolean)[0];
  if (!segment) return null;
  return SUPPORTED_LANGUAGES.includes(segment.toLowerCase()) ? segment.toLowerCase() : null;
}

/**
 * Meilleure langue selon le navigateur.
 *
 * `navigator.languages` est ordonné par préférence de l'utilisateur et disponible
 * partout (desktop et mobile) ; on le parcourt dans l'ordre et on prend la
 * première langue qu'on sait servir. `navigator.language` sert de filet pour les
 * rares moteurs qui n'exposent pas le tableau.
 */
export function detectBrowserLanguage(): string | null {
  if (typeof navigator === 'undefined') return null;
  const candidates: string[] = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
    // Safari ancien / IE : propriétés non standard, ignorées par TS.
    (navigator as any).userLanguage,
    (navigator as any).browserLanguage,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const match = normalizeLanguage(candidate);
    if (match) return match;
  }
  return null;
}

/** Langue stockée après un choix explicite de l'utilisateur. */
export function storedLanguage(): string | null {
  try {
    return normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Langue à utiliser au chargement : l'URL prime (un lien partagé impose sa
 * langue), puis le choix explicite déjà mémorisé, puis le navigateur, puis `fr`.
 */
export function resolveInitialLanguage(): string {
  return languageFromPath() ?? storedLanguage() ?? detectBrowserLanguage() ?? DEFAULT_LANGUAGE;
}

/** Remplace (ou ajoute) le préfixe de langue d'un chemin. */
export function withLanguagePrefix(pathname: string, language: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0].toLowerCase())) {
    segments.shift();
  }
  return `/${[language, ...segments].join('/')}`;
}
